/*
 * Copyright (c) 2021. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import type { TokenClaims } from '@azure/msal-common';
import {
  type AddFileResponse,
  type ArcgisToken,
  BASE_CONFIG_ID,
  type COST_DB_TYPES,
  type CompareResponse,
  type ComponentId,
  type CoreService,
  type CreateParcelsCheck,
  type DeleteFolderResponse,
  type EnvInfo,
  type FlowRunLogs,
  type FlowRunStatus,
  IDS_QUERY_PARAM,
  type ImageOverlayRef,
  type InsertErrorResponse,
  type ListResultsQuery,
  type ManufacturerRecord,
  type NotificationSeverity,
  type NotificationsQueryResult,
  type OpportunityListEntry,
  type OrgIds,
  type PlantConfiguration,
  type PowerAlphaRequest,
  type PrefectFlow,
  type PrevRunRequests,
  type ProjectResultsSummaryResponseEntry,
  type QueryComponentsSearchParams,
  type QueryConfigsSearchParams,
  type ScenarioQueryParams,
  type ScenarioRecord,
  type Task,
  type Workflow,
  type WorkflowMeta,
} from '@poweralpha/common';
import ky, { type SearchParamsOption } from 'ky';
import { pca } from './authClient';
import { loginRequest } from './authConfig';
import type { BatchResultsColumns } from './project/batch/data';

export type { QueryConfigsSearchParams } from '@poweralpha/common';

// THE ROUTES IN HERE SHOULD BE MOVED TO api/ folder gradually

// Allow external modules to set our bearer token function
export async function getBearerToken() {
  const instance = await pca;
  const accessTokenRequest = {
    ...loginRequest,
    account: instance.getAllAccounts().at(0),
  };

  try {
    let res = await instance.acquireTokenSilent(accessTokenRequest);
    const { exp } = res.idTokenClaims as TokenClaims;

    // Force refresh if the ID token has expired
    if (exp !== undefined && exp * 1000 <= Date.now()) {
      res = await instance.acquireTokenSilent({
        ...accessTokenRequest,
        forceRefresh: true,
      });
    }

    return res.idToken;
  } catch (_e) {
    await instance.acquireTokenRedirect(loginRequest);
    // This code will never be reached because of the page redirect
    return '';
  }
}

export async function getAuthHeader(): Promise<string> {
  const token = await getBearerToken();
  return `Bearer ${token}`;
}

export const api = ky.extend({
  prefixUrl: '/api/v1',
  timeout: 30000,
  hooks: {
    beforeRequest: [
      async (request) => {
        if (getBearerToken !== undefined) {
          const auth = await getAuthHeader();
          request.headers.set('Authorization', auth);
        }
      },
    ],
  },
});

export const getEnv = async (): Promise<EnvInfo> => api.get('health/env').json();

// helper function for query configs
export const getQueryConfigsSearchParams = (
  queryOptions?: QueryConfigsSearchParams,
  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: fine
): SearchParamsOption => {
  if (!queryOptions) return { orderby: 'modified_at', descending: 'true' };

  const { name, projectId, id, selectedIds, orderby, descending, startDate, endDate } =
    queryOptions;
  let searchParams: SearchParamsOption = {
    showArchived: queryOptions.showArchived ?? false,
    latestVersionOnly: queryOptions.latestVersionOnly ?? true,
    limit: queryOptions.limit ?? 100,
    offset: queryOptions.offset ?? 0,
    listOnly: queryOptions.listOnly ?? false,
    onlyMine: queryOptions.onlyMine ?? false,
    customAttrsStr: queryOptions.customAttrs
      ? queryOptions.customAttrs.map((pair) => pair.join(':')).join(',')
      : '',
    ...(id ? { id } : {}),
    ...(selectedIds ? { selectedIds } : {}),
    ...(projectId ? { projectId } : {}),
    ...(startDate ? { startDate } : {}), // for filtering by createdAt range, e.g. '2023-01-01:2023-01-31'
    ...(endDate ? { endDate } : {}), // for filtering by createdAt range, e.g. '2023-01-01:2023-01-31'
  };

  if (name && name !== '') {
    searchParams = { ...searchParams, name };
  } else {
    const order = orderby
      ? { orderby, descending: `${descending ?? true}` }
      : { orderby: 'modified_at', descending: 'true' };
    searchParams = { ...searchParams, ...order };
  }

  return searchParams;
};

export const getQueryComponentsSearchParams = <ListOnly extends boolean = false>(
  opts?: QueryComponentsSearchParams<ListOnly>,
): SearchParamsOption => {
  if (!opts) return {};
  const { search, offset, limit, listOnly, showArchived } = opts;
  return {
    search: search ?? '',
    listOnly: listOnly ?? false,
    showArchived: showArchived ?? false,
    limit: limit ?? 1000,
    offset: offset ?? 0,
  };
};

export const runPowerAlphaSimulation = async (
  paRequest: PowerAlphaRequest,
  plantConfiguration: Array<PlantConfiguration>,
): Promise<Task> =>
  api
    .post(`simulate?plant_configuration=${plantConfiguration.join('+')}`, {
      json: { paRequest },
    })
    .json();

export const runPowerAlphaBatchSimulation = async (
  form: FormData,
  plantConfiguration: Array<PlantConfiguration>,
): Promise<Task> =>
  api
    .post(`simulate/batch?plant_configuration=${plantConfiguration.join('+')}`, {
      body: form,
    })
    .json();

export const getPrevRunConfigurations = async (
  scenarioId: string | null,
): Promise<PrevRunRequests> => api.get(`results/${scenarioId}/configs`).json();

export const getResult = async <T = ScenarioRecord>(
  id: string | null | undefined,
  searchParams: Omit<ScenarioQueryParams, 'id'>,
  idKey: 'run_id' | 'scenario_id' = 'run_id',
  excludeTs = false,
): Promise<T> =>
  api
    .get(`results/${id}`, {
      searchParams: { ...searchParams, id_key: idKey, exclude_ts: excludeTs },
    })
    .json();

export const getMultipleResults = async <T = ScenarioRecord>(
  ids: Array<string>,
  searchParams: ScenarioQueryParams,
  idKey: 'run_id' | 'scenario_id' = 'run_id',
  excludeTs = false,
): Promise<Array<T>> => {
  const { id, id_key, exclude_ts, ...queryKeys } = searchParams;
  const querySearchParams = Object.entries(queryKeys)
    .filter(([, include]) => include)
    .map(([k, v]) => `${k}=${v}`);
  return api
    .get(
      `results/multiple?id=${ids.join('&id=')}&id_key=${idKey}&exclude_ts=${excludeTs}${
        querySearchParams.length ? `&${querySearchParams.join('&')}` : ''
      }`,
    )
    .json();
};

export interface ServiceListResultsSearchParams {
  limit?: number;
  offset?: number;
  onlyMine?: boolean;
  startDate?: string | null;
  endDate?: string | null;
  search?: string | null;
  projectId?: string;
  onlyConfigs?: boolean;
  bodOnly?: boolean;
  selectedIds?: string;
}

export const getServiceSearchParams = (
  opts?: ServiceListResultsSearchParams,
): Record<string, number | string | boolean> => {
  if (!opts) return {};
  return {
    limit: opts.limit ?? 100,
    offset: opts.offset ?? 0,
    onlyMine: opts.onlyMine ?? false,
    onlyConfigs: opts.onlyConfigs ?? false,
    bodOnly: opts.bodOnly ?? false,
    ...(opts.startDate ? { startDate: opts.startDate } : {}),
    ...(opts.endDate ? { endDate: opts.endDate } : {}),
    ...(opts.search ? { search: opts.search } : {}),
    ...(opts.projectId ? { projectId: opts.projectId } : {}),
    ...(opts.selectedIds ? { selectedIds: opts.selectedIds } : {}),
  };
};

export const getResults = async <T = ScenarioRecord>(
  serviceName: CoreService,
  opts?: ServiceListResultsSearchParams,
): Promise<ListResultsQuery<T>> =>
  api.get('results', { searchParams: { serviceName, ...getServiceSearchParams(opts) } }).json();

export const getWorkflowResult = async <T extends WorkflowMeta = null, Add = unknown>(
  id: string | null | undefined,
): Promise<Workflow<T> & Add> => api.get(`workflows/${id}`).json();

export const postWorkflowMeta = async <T extends WorkflowMeta = WorkflowMeta>(
  id: string,
  meta: Record<string, unknown>,
  opts?: { unset?: string[] },
): Promise<Workflow<T>> =>
  api.post(`workflows/${id}/meta`, { json: { meta, unset: opts?.unset } }).json();

export const getWorkflowData = async (
  name: string,
  ext: 'csv' | 'xlsx' = 'csv',
): Promise<BatchResultsColumns> =>
  api.get('workflows/pa-batch/data', { searchParams: { name, ext } }).json();

export const getMultipleWorkflowData = async (
  ids: Array<string>,
  ext: 'csv' | 'xlsx' = 'csv',
  type?: PlantConfiguration.PV | PlantConfiguration.BESS,
): Promise<BatchResultsColumns> => {
  const searchParams: Record<string, string> = { ids: ids.join(','), ext };
  if (type) {
    searchParams.type = type;
  }
  return api.get('workflows/pa-batch/multi-data', { searchParams }).json();
};

export const getWorkflowResults = async <T extends WorkflowMeta>(
  flowNames: readonly PrefectFlow[],
  projectId?: string,
  limit = 100,
  offset = 0,
  isComplete = false,
  opts?: { search?: string | null },
): Promise<ListResultsQuery<Workflow<T>>> =>
  api
    .get(
      `workflows?${
        projectId ? `project_id=${projectId}&` : ''
      }limit=${limit}&offset=${offset}&isComplete=${isComplete}${
        opts?.search ? `&search=${encodeURIComponent(opts.search)}` : ''
      }&flow_names=${flowNames.map((n) => encodeURIComponent(n)).join('&flow_names=')}`,
    )
    .json();

export const checkForStatus = async (id: string | null | undefined): Promise<FlowRunStatus> => {
  if (id === null) {
    throw new Error('id needs to be defined');
  }
  return api.get(`flows/runs/${id}`).json();
};

export const getComponentsRunLogs = async (): Promise<FlowRunLogs[]> =>
  api.get('components/logs').json();

export const getOrgIds = async (): Promise<OrgIds> => api.get('simulations/org-ids').json();

export const getScenariosResultsSummary = async (
  scenarioIds: Array<string>,
): Promise<ListResultsQuery<ProjectResultsSummaryResponseEntry>> =>
  api
    .get('results/summary', {
      searchParams: { scenarioIds: scenarioIds.join(',') } as SearchParamsOption,
      timeout: 30e3,
    })
    .json();

export const getOpportunitiesList = async (
  name: string | null = null,
  stage: string | null = null,
  showClosedLost = false,
): Promise<OpportunityListEntry[]> => {
  const searchParams: SearchParamsOption = {
    showClosedLost,
    ...(name != null && name !== '' ? { name } : {}),
    ...(stage != null && stage !== '' ? { stage } : {}),
  };
  return api.get('opportunities', { searchParams }).json();
};

export const getOpportunityMeta = async (
  opportunityId: string | null,
): Promise<OpportunityListEntry> => api.get(`opportunities/${opportunityId}`).json();

export const parseCostDatabase = async (
  data: FormData,
  type: COST_DB_TYPES,
  user: string,
): Promise<ComponentId> =>
  api.post(`proxy/${type}/costing/db/upload`, { body: data, headers: { user } }).json();

export const editCostDatabase = async (
  data: FormData,
  type: COST_DB_TYPES,
  id: string,
  user: string,
): Promise<void> =>
  api.post(`proxy/${type}/costing/db/upload/${id}`, { body: data, headers: { user } }).json();

export const archiveCostDatabase = async (id: string | undefined): Promise<void> =>
  api.post(`common/cost-dbs/${id}/archive`).json();

export const lockCostDatabase = async (id: string): Promise<void> => {
  await api.post(`common/cost-dbs/${id}/validate`);
};

export const getRunsStatus = async <T = Record<string, unknown>>(opts: {
  runIds?: string[];
  flowNames?: Array<PrefectFlow>;
  queryByUser?: boolean;
  limit?: number;
  offset?: number;
  reverse?: boolean;
}): Promise<ListResultsQuery<FlowRunStatus<T>>> => {
  const runSearchParams = opts.runIds
    ? `run_id=${opts.runIds?.map((n) => encodeURIComponent(n)).join('&run_id=')}`
    : undefined;
  const flowNamesSearchParams = opts.flowNames
    ? `flow_name=${opts.flowNames?.map((n) => encodeURIComponent(n)).join('&flow_name=')}`
    : undefined;
  const queryByUserSearchParam = opts.queryByUser ? `queryByUser=${opts.queryByUser}` : undefined;
  const limitSearchParam = opts.limit ? `limit=${opts.limit}` : undefined;
  const offsetSearchParam = opts.offset ? `offset=${opts.offset}` : undefined;
  const reverseSearchParam = `reverse=${opts.reverse ?? false}`;
  return api
    .get(
      `simulations/runs-status?${[
        runSearchParams,
        flowNamesSearchParams,
        queryByUserSearchParam,
        limitSearchParam,
        offsetSearchParam,
        reverseSearchParam,
      ]
        .filter(Boolean)
        .join('&')}`,
    )
    .json();
};

export const getScenarioComparison = async (scenarioIds: string[]): Promise<ScenarioRecord[]> =>
  api.get(`results/compare?scenario=${scenarioIds.join('&scenario=')}`).json();

export const deleteSharepointItem = async (
  path: string | undefined,
): Promise<DeleteFolderResponse> =>
  api.get('sharepoint/delete', { searchParams: { path: path ?? '' } }).json();

export const addFilesToSharepoint = async (
  path: string | undefined,
  files: FormData,
): Promise<Array<AddFileResponse>> =>
  api.post('sharepoint/files', { searchParams: { path: path ?? '' }, body: files }).json();

export const getNotifications = async (
  limit = 30,
  offset = 0,
  severity: NotificationSeverity | null = null,
): Promise<NotificationsQueryResult> =>
  api
    .get('notifications', { searchParams: { limit, offset, ...(severity ? { severity } : {}) } })
    .json();

export const markNotificationAsViewed = async (id: string | undefined): Promise<void> =>
  api.post(`notifications/${id}/viewed`).json();

export const markAllNotificationAsViewed = async (): Promise<{ count: number }> =>
  api.post('notifications/viewed').json();

export const clearAllNotification = async (): Promise<{ count: number }> =>
  api.post('notifications/clear').json();

export const addManufacturers = async (
  manufacturerType: ManufacturerRecord['component_type'],
  newManufacturers: Array<string>,
): Promise<Array<ManufacturerRecord>> =>
  api.post(`common/manufacturers/${manufacturerType}`, { json: { newManufacturers } }).json();

export const getManufacturers = async (
  manufacturerType: ManufacturerRecord['component_type'],
): Promise<Array<ManufacturerRecord>> => api.get(`common/manufacturers/${manufacturerType}`).json();

export const getArcgisToken = async (): Promise<ArcgisToken> => api.get('gis/token').json();

export const getGisImageRef = async (
  id: string | undefined,
  endedTime: string | undefined | null,
): Promise<ImageOverlayRef> => api.get(`gis/image-ref/${id}/${endedTime}`).json();

export const getGisGeoJson = async <T>(
  requestId: string | undefined,
  fileName?: string,
): Promise<T> => api.get(`gis/geojson/${requestId}/${fileName ?? ''}`).json();

export const createParcelsFromSite = async (
  siteName: string,
  user: string | undefined,
  create = false,
): Promise<
  { created_ids: Array<string>; errors: Array<InsertErrorResponse> } | Array<CreateParcelsCheck>
> =>
  api
    .post('proxy/gis/create-parcels', {
      json: { site_name: siteName },
      searchParams: { create },
      headers: { user },
      timeout: 60e3,
    })
    .json();

export const getConfigurationsForCompare = async <T>(
  path: string,
  ids: string,
  baseConfigId: string,
): Promise<CompareResponse<T>> =>
  api
    .get(`${path}/configs-for-compare`, {
      searchParams: {
        [IDS_QUERY_PARAM]: ids,
        ...(baseConfigId ? { [BASE_CONFIG_ID]: baseConfigId } : {}),
      },
    })
    .json();

export const getResultsForCompare = async <T>(
  path: string,
  ids: string,
  baseConfigId: string,
): Promise<CompareResponse<T>> =>
  api
    .get(`${path}/results-for-compare`, {
      searchParams: {
        [IDS_QUERY_PARAM]: ids,
        ...(baseConfigId ? { [BASE_CONFIG_ID]: baseConfigId } : {}),
      },
    })
    .json();
